<script setup lang="ts">
import type {
  FastCheckoutMethod,
  FastCheckoutSurface,
} from '@design-system/composables/useFastCheckoutMethods'

const props = withDefaults(
  defineProps<{
    method: FastCheckoutMethod
    isSdkReady: boolean
    containerId: string
    surface?: FastCheckoutSurface
    compact?: boolean
    loading?: boolean
    disabled?: boolean
    isAwaitingSdk?: boolean
  }>(),
  {
    surface: 'pdp',
    compact: false,
    isAwaitingSdk: false,
  }
)

const emit = defineEmits<{
  click: []
  ready: []
}>()

const appleButtonColor = computed(() =>
  props.surface === 'layer' ? 'black' : 'white-with-line'
)
const walletButtonType = computed(() => (props.compact ? 'plain' : 'buy'))

const pdpWalletClass =
  'h-[52px] !min-h-[52px] [&_button]:!h-[52px] [&_iframe]:!h-[52px]'

/**
 * On the PDP the shopper only ever sees our own method button: the SDK button
 * is mounted on top of it, transparent, and takes the clicks once it has
 * rendered. Landing on the PDP, picking the first size or opening the size
 * selector therefore never shows a loader or a button swap.
 */
const isOverlay = computed(() => props.surface === 'pdp')

/**
 * PayPal reads the express order only when clicked (`createOrder` waits for
 * the one being prepared), so on the PDP its button is rendered on landing and
 * takes clicks as soon as a size is picked. Apple Pay and Google Pay need the
 * order and its Adyen session first, so they only take clicks once the order
 * is ready for the selected size (`isSdkReady`).
 */
const isPayPalOverlay = computed(
  () => isOverlay.value && props.method === 'payPal'
)
const shouldMountSdk = computed(() => props.isSdkReady || isPayPalOverlay.value)
const isSdkActive = ref(false)
const isSdkClickable = computed(
  () =>
    isSdkActive.value &&
    !props.disabled &&
    (props.isSdkReady || (isPayPalOverlay.value && props.isAwaitingSdk))
)

/**
 * Until the SDK button on top takes the click, ours is shown disabled instead
 * of silently swallowing it, and it is enabled again as soon as it does.
 */
const isMethodButtonDisabled = computed(
  () =>
    props.disabled ||
    (isOverlay.value && props.isAwaitingSdk && !isSdkClickable.value)
)

watch([shouldMountSdk, () => props.method], () => {
  isSdkActive.value = false
})

const handleSdkReady = () => {
  isSdkActive.value = true
  emit('ready')
}
</script>

<template>
  <div
    :class="[
      surface === 'pdp' && pdpWalletClass,
      isOverlay &&
        'has-[:focus-visible]:outline-primitives-black relative has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
    ]"
  >
    <div
      v-if="shouldMountSdk"
      :class="{
        'absolute inset-0 z-[1] overflow-hidden opacity-0': isOverlay,
        'pointer-events-none': isOverlay && !isSdkClickable,
      }"
      :inert="(isOverlay && !isSdkClickable) || undefined"
    >
      <CartPaypalExpressButton
        v-if="method === 'payPal'"
        source="express"
        button-color="white"
        button-label="paypal"
        :button-height="surface === 'pdp' ? 52 : 48"
        :container-id="containerId"
        @ready="handleSdkReady"
      />
      <CheckoutApplePayPayment
        v-else-if="method === 'applePay'"
        source="express"
        :button-color="appleButtonColor"
        :button-type="walletButtonType"
        :container-id="containerId"
        @ready="handleSdkReady"
      />
      <CheckoutGooglePayPayment
        v-else-if="method === 'googlePay'"
        source="express"
        button-color="white"
        :button-type="walletButtonType"
        :container-id="containerId"
        @ready="handleSdkReady"
      />
    </div>
    <PdpEcommerceBlockProductFastCheckoutMethodButton
      v-if="isOverlay || !shouldMountSdk"
      :method="method"
      :surface="surface"
      :compact="compact"
      :loading="loading"
      :disabled="isMethodButtonDisabled"
      :tabindex="isSdkClickable ? -1 : undefined"
      :aria-hidden="isSdkClickable || undefined"
      @click="emit('click')"
    />
  </div>
</template>
